/**
 * useLeaseWizard Hook
 * State management for the 9-step lease creation wizard
 */

import { useState, useCallback } from 'react';
import { useTrackEvent, ANALYTICS_EVENTS } from '@resida/analytics';
import type {
  WizardState,
  WizardInputs,
  LandlordContact,
  JurisdictionResponse,
  PetPolicy,
  PetDetail,
  AssistanceAnimal,
  CoSigner,
  PreviewResponse,
  LeaseDocumentDetailResponse,
  GenerateRequest,
  PreviewRequest,
  UpdateDraftRequest,
  AssembledContentSnapshot,
} from '../../types/lease-generation';
import type { MoveInSuggestion } from '../../types/move-in';
import {
  resolveJurisdiction,
  previewLease as previewLeaseApi,
  generateLease as generateLeaseApi,
  sendLeaseDocumentForSigning as sendForSigningApi,
  getLeaseWizardDraft,
  saveLeaseWizardDraft,
  createLeaseWizardDraft,
  getLeaseDefaults,
  validateLeaseAnchors,
  ApiClientError,
} from '../../lib/api';

function initialState(unitId: string): WizardState {
  return {
    currentStep: 1,
    unitId,
    jurisdictionInfo: null,
    selectedTenantIds: [],
    inputs: { leaseType: 'fixed-term' },
    landlordContact: {
      fullName: '',
      mailingAddress: '',
      city: '',
      state: '',
      zipCode: '',
      email: '',
      phone: '',
      contactMethods: ['email'],
    },
    draftDocumentId: undefined,
    pets: { allowed: false, animals: [], assistanceAnimals: [] },
    coSigners: [],
    parking: { included: false },
    utilities: {
      tenantArranged: ['Electricity', 'Gas / Heat', 'Water & Sewer', 'Garbage / Recycling', 'Internet / Cable'],
      includedInRent: [],
      chargedBack: [],
    },
    shortTermRentalProhibited: false,
    smokingPolicy: { prohibited: true },
    rentersInsurance: { required: true },
    isLoading: false,
    isSaving: false,
    error: null,
    conflictCode: null,
    previewContent: null,
    generatedDocument: null,
    editedContent: null,
    dirtyFields: new Set<string>(),
  };
}

/**
 * Build extra variables for core clause templates from wizard answers.
 * These control conditional sections in pet-policy, parking, utilities,
 * smoking, short-term-rental, and renters-insurance core clauses.
 */
function buildExtraVariables(state: WizardState): Record<string, unknown> {
  const { includedInRent, chargedBack, tenantArranged } = state.utilities;
  const landlordPaid = [...includedInRent, ...chargedBack];

  return {
    // Parking
    parking_included: state.parking.included,
    parking_space_number: state.parking.spaceNumber,
    parking_monthly_fee: state.parking.monthlyFee,

    // Utilities — preserve the three-tier distinction
    landlord_paid_utilities: landlordPaid.length > 0 ? landlordPaid.join(', ') : '',
    tenant_paid_utilities: tenantArranged.length > 0 ? tenantArranged.join(', ') : '',
    included_in_rent_utilities: includedInRent.length > 0 ? includedInRent.join(', ') : '',
    charged_back_utilities: chargedBack.length > 0 ? chargedBack.join(', ') : '',

    // Short-term rental
    short_term_rental_prohibited: state.shortTermRentalProhibited,

    // Smoking
    smoking_prohibited: state.smokingPolicy.prohibited,
    smoking_allowed_areas: state.smokingPolicy.allowedAreas,

    // Renters insurance
    renters_insurance_required: state.rentersInsurance.required,
    insurance_min_liability_amount: state.rentersInsurance.minLiabilityAmount,
  };
}

/**
 * Reverse-map extraVariables back into wizard state fields.
 * Used when loading saved property defaults.
 */
function applyExtraVariablesToState(
  ev: Record<string, unknown>,
): Partial<WizardState> {
  const result: Partial<WizardState> = {};

  // Parking
  if (ev.parking_included !== undefined) {
    result.parking = {
      included: !!ev.parking_included,
      spaceNumber: ev.parking_space_number as string | undefined,
      monthlyFee: ev.parking_monthly_fee as number | undefined,
    };
  }

  // Utilities — use the granular fields if available, fall back to landlord_paid for compat
  if (ev.included_in_rent_utilities !== undefined || ev.charged_back_utilities !== undefined || ev.tenant_paid_utilities !== undefined) {
    const includedInRent = typeof ev.included_in_rent_utilities === 'string' && ev.included_in_rent_utilities
      ? ev.included_in_rent_utilities.split(', ')
      : [];
    const chargedBack = typeof ev.charged_back_utilities === 'string' && ev.charged_back_utilities
      ? ev.charged_back_utilities.split(', ')
      : [];
    const tenantPaid = typeof ev.tenant_paid_utilities === 'string' && ev.tenant_paid_utilities
      ? ev.tenant_paid_utilities.split(', ')
      : [];
    result.utilities = {
      tenantArranged: tenantPaid,
      includedInRent,
      chargedBack,
    };
  } else if (ev.landlord_paid_utilities !== undefined) {
    // Legacy fallback: no granular fields, default landlord_paid to includedInRent
    const landlordPaid = typeof ev.landlord_paid_utilities === 'string' && ev.landlord_paid_utilities
      ? ev.landlord_paid_utilities.split(', ')
      : [];
    const allUtilities = ['Electricity', 'Gas / Heat', 'Water & Sewer', 'Garbage / Recycling', 'Internet / Cable'];
    const tenantPaid = allUtilities.filter(u => !landlordPaid.includes(u));
    result.utilities = {
      tenantArranged: tenantPaid,
      includedInRent: landlordPaid,
      chargedBack: [],
    };
  }

  // Short-term rental
  if (ev.short_term_rental_prohibited !== undefined) {
    result.shortTermRentalProhibited = !!ev.short_term_rental_prohibited;
  }

  // Smoking policy
  if (ev.smoking_prohibited !== undefined) {
    result.smokingPolicy = {
      prohibited: !!ev.smoking_prohibited,
      allowedAreas: ev.smoking_allowed_areas as string | undefined,
    };
  }

  // Renters insurance
  if (ev.renters_insurance_required !== undefined) {
    result.rentersInsurance = {
      required: !!ev.renters_insurance_required,
      minLiabilityAmount: ev.insurance_min_liability_amount as number | undefined,
    };
  }

  return result;
}

/**
 * Apply a single suggestion's proposedValue to the correct WizardState path.
 *
 * Field mapping (suggestion.field → WizardState path):
 *   inputs.rentAmount         → state.inputs.rentAmount  (expects value in dollars; may be cents from API)
 *   inputs.startDate          → state.inputs.startDate   (ISO string)
 *   inputs.endDate            → state.inputs.endDate     (ISO string)
 *   inputs.securityDeposit    → state.inputs.securityDeposit
 *   inputs.leaseType          → state.inputs.leaseType
 *   extraVariables.smoking_prohibited         → state.smokingPolicy.prohibited
 *   extraVariables.parking_monthly_fee        → state.parking.monthlyFee
 *   extraVariables.parking_included           → state.parking.included
 *   extraVariables.renters_insurance_required → state.rentersInsurance.required
 */
function applyFieldPreFill(state: WizardState, field: string, value: unknown): WizardState {
  if (field.startsWith('inputs.')) {
    const inputKey = field.slice('inputs.'.length) as keyof WizardInputs;
    // Pre-fill fills gaps only — never clobber a value already present from a saved draft
    // or property defaults. (This is what makes auto-prefill safe to run on load.)
    const existing = state.inputs[inputKey];
    if (existing != null && existing !== '') return state;
    return { ...state, inputs: { ...state.inputs, [inputKey]: value } };
  }
  if (field === 'extraVariables.smoking_prohibited') {
    return { ...state, smokingPolicy: { ...state.smokingPolicy, prohibited: !!value } };
  }
  if (field === 'extraVariables.parking_included') {
    return { ...state, parking: { ...state.parking, included: !!value } };
  }
  if (field === 'extraVariables.parking_monthly_fee') {
    return { ...state, parking: { ...state.parking, monthlyFee: typeof value === 'number' ? value : undefined } };
  }
  if (field === 'extraVariables.renters_insurance_required') {
    return { ...state, rentersInsurance: { ...state.rentersInsurance, required: !!value } };
  }
  if (field === 'pets.animals') {
    const animals = value as PetDetail[];
    // Defensive normalize: guard against legacy pre-fill that has no `type`.
    // Spread `a` after the default so existing `type` values are preserved.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const normalized = animals.map(a => ({ ...a, type: (a as any).type ?? ('dog' as PetDetail['type']) }));
    if (normalized.length === 0) {
      return { ...state, pets: { ...state.pets, animals: [] } };
    }
    return {
      ...state,
      pets: {
        ...state.pets,
        allowed: true,
        animals: normalized,
        _prefilled: true,
      },
    };
  }
  if (field === 'pets.claimedPetsText') {
    return {
      ...state,
      pets: { ...state.pets, _claimedPetsHint: value as string },
    };
  }
  if (field === 'pets.allowed') {
    return {
      ...state,
      pets: { ...state.pets, allowed: !!value },
    };
  }
  // Unrecognized field — return unchanged
  return state;
}

/** Same shape used by Step8ReviewPreview's `previewToSnapshot` — kept in sync deliberately. */
function snapshotFromPreview(preview: PreviewResponse): AssembledContentSnapshot {
  return {
    coreLease: preview.coreLease,
    disclosures: preview.disclosures.map(d => ({ id: d.id, content: d.content })),
    addendums: preview.addendums.map(a => ({ slug: a.slug, content: a.content })),
  };
}

function buildRequestInputs(state: WizardState): WizardInputs & { landlordContact: LandlordContact } {
  const { inputs, selectedTenantIds } = state;
  const today = new Date().toISOString().split('T')[0];

  return {
    leaseType: inputs.leaseType ?? 'fixed-term',
    startDate: (inputs.startDate ?? today) as string,
    endDate: inputs.endDate,
    autoRenewal: inputs.autoRenewal ?? false,
    rentAmount: (inputs.rentAmount ?? 0) * 100,
    securityDeposit: (inputs.securityDeposit ?? 0) * 100,
    lateFeeType: inputs.lateFeeType ?? 'none',
    lateFeeAmount: inputs.lateFeeType === 'percentage'
      ? (inputs.lateFeeAmount ?? 0)
      : (inputs.lateFeeAmount ?? 0) * 100,
    lateFeeMaxAmount: inputs.lateFeeMaxAmount != null ? inputs.lateFeeMaxAmount * 100 : undefined,
    gracePeriodDays: inputs.gracePeriodDays ?? 0,
    paymentDueDay: inputs.paymentDueDay ?? 1,
    entryNoticeHours: inputs.entryNoticeHours ?? 24,
    sublettingPolicy: inputs.sublettingPolicy ?? 'not-allowed',
    guestPolicy: inputs.guestPolicy,
    alterationsPolicy: inputs.alterationsPolicy,
    tenantIds: selectedTenantIds,
    landlordContact: {
      fullName: state.landlordContact.fullName,
      mailingAddress: state.landlordContact.mailingAddress,
      city: state.landlordContact.city,
      state: state.landlordContact.state,
      zipCode: state.landlordContact.zipCode,
      email: state.landlordContact.email,
      phone: state.landlordContact.phone,
      contactMethods: state.landlordContact.contactMethods,
    },
  };
}

export function useLeaseWizard(unitId: string, existingLeaseId?: string) {
  const track = useTrackEvent();
  const [state, setState] = useState<WizardState>(() => {
    const s = initialState(unitId);
    if (existingLeaseId) s.draftLeaseId = existingLeaseId;
    return s;
  });

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const goToStep = useCallback((n: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9) => {
    setState(prev => ({ ...prev, currentStep: n, error: null, conflictCode: null }));
  }, []);

  const updateInputs = useCallback((partial: Partial<WizardState['inputs']>) => {
    setState(prev => {
      // Mark all fields in this partial update as dirty (landlord-edited)
      const newDirty = new Set(prev.dirtyFields);
      for (const key of Object.keys(partial)) {
        newDirty.add(`inputs.${key}`);
      }
      return { ...prev, inputs: { ...prev.inputs, ...partial }, dirtyFields: newDirty };
    });
  }, []);

  const setSelectedTenantIds = useCallback((ids: string[]) => {
    setState(prev => ({ ...prev, selectedTenantIds: ids }));
  }, []);

  // Pets
  const updatePets = useCallback((update: Partial<PetPolicy>) => {
    setState(prev => ({ ...prev, pets: { ...prev.pets, ...update } }));
  }, []);

  const addAnimal = useCallback((animal: PetDetail) => {
    setState(prev => ({ ...prev, pets: { ...prev.pets, animals: [...prev.pets.animals, animal] } }));
  }, []);

  const updateAnimal = useCallback((index: number, update: Partial<PetDetail>) => {
    setState(prev => {
      const animals = prev.pets.animals.map((a, i) => i === index ? { ...a, ...update } : a);
      return { ...prev, pets: { ...prev.pets, animals } };
    });
  }, []);

  const removeAnimal = useCallback((index: number) => {
    setState(prev => {
      const animals = prev.pets.animals.filter((_, i) => i !== index);
      return { ...prev, pets: { ...prev.pets, animals } };
    });
  }, []);

  const addAssistanceAnimal = useCallback((animal: AssistanceAnimal) => {
    setState(prev => ({
      ...prev,
      pets: { ...prev.pets, assistanceAnimals: [...prev.pets.assistanceAnimals, animal] },
    }));
  }, []);

  const updateAssistanceAnimal = useCallback((index: number, update: Partial<AssistanceAnimal>) => {
    setState(prev => {
      const assistanceAnimals = prev.pets.assistanceAnimals.map(
        (a, i) => i === index ? { ...a, ...update } : a
      );
      return { ...prev, pets: { ...prev.pets, assistanceAnimals } };
    });
  }, []);

  const removeAssistanceAnimal = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      pets: {
        ...prev.pets,
        assistanceAnimals: prev.pets.assistanceAnimals.filter((_, i) => i !== index),
      },
    }));
  }, []);

  // Co-signers
  const addCoSigner = useCallback((coSigner: CoSigner) => {
    setState(prev => ({ ...prev, coSigners: [...prev.coSigners, coSigner] }));
  }, []);

  const updateCoSigner = useCallback((index: number, update: Partial<CoSigner>) => {
    setState(prev => {
      const coSigners = prev.coSigners.map((c, i) => i === index ? { ...c, ...update } : c);
      return { ...prev, coSigners };
    });
  }, []);

  const removeCoSigner = useCallback((index: number) => {
    setState(prev => ({ ...prev, coSigners: prev.coSigners.filter((_, i) => i !== index) }));
  }, []);

  // Parking
  const updateParking = useCallback((partial: Partial<WizardState['parking']>) => {
    setState(prev => ({
      ...prev,
      parking: { ...prev.parking, ...partial },
    }));
  }, []);

  // Utilities
  const updateUtilities = useCallback((partial: Partial<WizardState['utilities']>) => {
    setState(prev => ({
      ...prev,
      utilities: { ...prev.utilities, ...partial },
    }));
  }, []);

  // Short-term rental
  const setShortTermRentalProhibited = useCallback((prohibited: boolean) => {
    setState(prev => ({ ...prev, shortTermRentalProhibited: prohibited }));
  }, []);

  // Smoking policy
  const updateSmokingPolicy = useCallback((partial: Partial<WizardState['smokingPolicy']>) => {
    setState(prev => ({
      ...prev,
      smokingPolicy: { ...prev.smokingPolicy, ...partial },
    }));
  }, []);

  // Renters insurance
  const updateRentersInsurance = useCallback((partial: Partial<WizardState['rentersInsurance']>) => {
    setState(prev => ({
      ...prev,
      rentersInsurance: { ...prev.rentersInsurance, ...partial },
    }));
  }, []);

  // Landlord contact
  const updateLandlordContact = useCallback((partial: Partial<LandlordContact>) => {
    setState(prev => ({
      ...prev,
      landlordContact: { ...prev.landlordContact, ...partial },
    }));
  }, []);

  const initLandlordContact = useCallback((
    managerName: string,
    managerEmail: string,
    address?: { mailingAddress?: string; city?: string; state?: string; zipCode?: string; phone?: string },
  ) => {
    setState(prev => {
      // Only init if the contact hasn't been set yet (don't overwrite user changes)
      if (prev.landlordContact.fullName !== '') return prev;
      return {
        ...prev,
        landlordContact: {
          ...prev.landlordContact,
          fullName: managerName,
          email: managerEmail,
          mailingAddress: address?.mailingAddress ?? prev.landlordContact.mailingAddress,
          city: address?.city ?? prev.landlordContact.city,
          state: address?.state ?? prev.landlordContact.state,
          zipCode: address?.zipCode ?? prev.landlordContact.zipCode,
          phone: address?.phone ?? prev.landlordContact.phone,
        },
      };
    });
  }, []);

  /**
   * Apply pre-fill suggestions to wizard state.
   * Only fills fields that the landlord has NOT already edited (not in dirtyFields).
   * Skips dismissed/snoozed suggestions (preference !== null).
   */
  const applyPreFill = useCallback((suggestions: MoveInSuggestion[]) => {
    setState(prev => {
      let next = prev;
      for (const s of suggestions) {
        // Skip if landlord has already touched this field
        if (prev.dirtyFields.has(s.field)) continue;
        // Skip dismissed/snoozed
        if (s.preference !== null) continue;
        next = applyFieldPreFill(next, s.field, s.proposedValue);
      }
      return next;
    });
  }, []);

  const loadJurisdiction = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const result: JurisdictionResponse = await resolveJurisdiction(unitId);
      setState(prev => ({ ...prev, jurisdictionInfo: result, isLoading: false }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load jurisdiction info',
      }));
    }
  }, [unitId]);

  const loadDraft = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const draft = await getLeaseWizardDraft(unitId);
      if (draft) {
        const vars = draft.variables ?? {};
        const savedInputs = vars.wizardInputs as (Partial<WizardInputs> & { landlordContact?: LandlordContact; pets?: PetPolicy }) | undefined;
        const rawSavedStep = vars.wizardCurrentStep as number | undefined;
        // Draft migration guard: old step 9 (Edit Document) → new step 8 (Review & Edit)
        // old step 10 (Generate & Send) → new step 9 (Generate & Send)
        const savedStep = rawSavedStep === 10 ? 9 : rawSavedStep === 9 ? 8 : rawSavedStep;
        const defaultPets: PetPolicy = { allowed: false, animals: [], assistanceAnimals: [] };
        const savedPets = savedInputs?.pets
          ? { ...defaultPets, ...savedInputs.pets }
          : undefined;

        setState(prev => ({
          ...prev,
          draftDocumentId: draft.id,
          draftLeaseId: draft.leaseId,
          ...(savedInputs ? { inputs: { ...prev.inputs, ...savedInputs } } : {}),
          ...(savedInputs?.tenantIds ? { selectedTenantIds: savedInputs.tenantIds as string[] } : {}),
          ...(savedStep ? { currentStep: savedStep as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 } : {}),
          // Restore landlordContact from saved inputs if present
          ...(savedInputs?.landlordContact ? { landlordContact: savedInputs.landlordContact as LandlordContact } : {}),
          // Restore pets from saved inputs (includes assistanceAnimals; defaults to [] for old drafts)
          ...(savedPets ? { pets: savedPets } : {}),
          // Restore extra variable state from saved inputs
          ...(vars ? applyExtraVariablesToState(vars) : {}),
          // Restore editedContent from draft if present
          editedContent: draft.editedContent ?? null,
          isLoading: false,
        }));
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load draft',
      }));
    }
  }, [unitId]);

  const loadDefaults = useCallback(async () => {
    try {
      const { defaults } = await getLeaseDefaults(unitId);
      if (!defaults) return;

      setState(prev => {
        // Skip defaults if a draft was already loaded (draft takes precedence)
        // or if the user has already entered financial values
        const hasDraft = prev.draftDocumentId !== undefined;
        const hasUserInputs = prev.inputs.rentAmount !== undefined;
        if (hasDraft || hasUserInputs) return prev;

        return {
          ...prev,
          inputs: { ...prev.inputs, ...defaults.inputs },
          // Restore landlordContact from saved defaults if present
          ...((defaults.inputs as Partial<WizardInputs & { landlordContact: LandlordContact }>).landlordContact
            ? { landlordContact: (defaults.inputs as Partial<WizardInputs & { landlordContact: LandlordContact }>).landlordContact as LandlordContact }
            : {}),
          // Restore extra variable state from saved defaults
          ...(defaults.extraVariables ? applyExtraVariablesToState(defaults.extraVariables) : {}),
        };
      });
    } catch {
      // Non-fatal — silently ignore if defaults can't be loaded
    }
  }, [unitId]);

  const saveDraft = useCallback(async () => {
    setState(prev => ({ ...prev, isSaving: true, error: null }));
    try {
      let docId = state.draftDocumentId;

      // Create a new draft if one doesn't exist yet
      if (!docId) {
        const created = await createLeaseWizardDraft(unitId, existingLeaseId);
        docId = created.id;
        setState(prev => ({ ...prev, draftDocumentId: docId }));
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _claimedPetsHint, _prefilled, ...petsForDraft } = state.pets;
      const draftData: UpdateDraftRequest = {
        partialInputs: { ...state.inputs, tenantIds: state.selectedTenantIds },
        currentStep: state.currentStep,
        landlordContact: state.landlordContact,
        pets: petsForDraft,
        coSigners: state.coSigners,
        editedContent: state.editedContent ?? undefined,
      };
      await saveLeaseWizardDraft(docId, draftData);
      setState(prev => ({ ...prev, isSaving: false }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        isSaving: false,
        error: err instanceof Error ? err.message : 'Failed to save draft',
      }));
    }
  }, [state.draftDocumentId, state.inputs, state.pets, state.coSigners, state.currentStep, state.landlordContact, state.selectedTenantIds, state.editedContent, unitId, existingLeaseId]);

  const previewLease = useCallback(async (): Promise<boolean> => {
    const reqInputs = buildRequestInputs(state);
    const extraVars = buildExtraVariables(state);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _claimedPetsHint, _prefilled, ...petsForPreview } = state.pets;

    const req: PreviewRequest = {
      unitId,
      inputs: reqInputs,
      pets: petsForPreview,
      coSigners: state.coSigners,
      extraVariables: extraVars,
    };

    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const result: PreviewResponse = await previewLeaseApi(req);
      setState(prev => {
        // If the landlord hasn't made any real inline edits yet, drop the old editedContent
        // snapshot so the preview surface re-seeds it from this fresh (corrected) result —
        // otherwise a re-preview after fixing an earlier step (e.g. landlord contact) keeps
        // rendering the stale first-pass content forever, since editedContent takes precedence.
        const priorSnapshot = prev.previewContent ? snapshotFromPreview(prev.previewContent) : null;
        const hasUnsavedEdits =
          prev.editedContent != null &&
          priorSnapshot != null &&
          JSON.stringify(prev.editedContent) !== JSON.stringify(priorSnapshot);
        return {
          ...prev,
          previewContent: result,
          editedContent: hasUnsavedEdits ? prev.editedContent : null,
          isLoading: false,
        };
      });
      return true;
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to generate preview',
      }));
      return false;
    }
  }, [state, unitId]);

  const generateLease = useCallback(async () => {
    const reqInputs = buildRequestInputs(state);
    const extraVars = buildExtraVariables(state);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _claimedPetsHint, _prefilled, ...petsForGenerate } = state.pets;

    const req: GenerateRequest = {
      unitId,
      draftDocumentId: state.draftDocumentId,
      leaseId: state.draftLeaseId,
      inputs: reqInputs,
      pets: petsForGenerate,
      coSigners: state.coSigners,
      extraVariables: extraVars,
      editedContent: state.editedContent ?? undefined,
    };

    setState(prev => ({ ...prev, isLoading: true, error: null, conflictCode: null, generatedDocument: null }));
    try {
      const result: LeaseDocumentDetailResponse = await generateLeaseApi(req);
      track(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, { step: 'lease' });
      track(ANALYTICS_EVENTS.LEASE_CREATED, {
        portfolio_id: '',
        property_id: '',
        unit_count: 1,
      });
      setState(prev => ({ ...prev, generatedDocument: result, isLoading: false }));
    } catch (err) {
      const conflictCode = err instanceof ApiClientError && err.code === 'TENANT_ALREADY_ON_ACTIVE_LEASE'
        ? 'TENANT_ALREADY_ON_ACTIVE_LEASE'
        : null;
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to generate lease',
        conflictCode,
      }));
    }
  }, [state, unitId, track]);

  const setEditedContent = useCallback((content: AssembledContentSnapshot | null) => {
    setState(prev => ({ ...prev, editedContent: content }));
  }, []);

  const validateAnchors = useCallback(async (): Promise<boolean> => {
    if (!state.editedContent) return true; // No edits = nothing to validate
    try {
      const result = await validateLeaseAnchors(unitId, state.editedContent);
      if (!result.isValid) {
        setState(prev => ({
          ...prev,
          error: `Missing required signature tokens: ${result.missingTokens.join(', ')}. These must remain in the document.`,
        }));
      }
      return result.isValid;
    } catch (err) {
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Failed to validate anchors',
      }));
      return false;
    }
  }, [state.editedContent, unitId]);

  const sendForSigning = useCallback(async () => {
    const { generatedDocument } = state;
    if (!generatedDocument) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const updated = await sendForSigningApi(generatedDocument.leaseId, generatedDocument.id);
      track(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, { step: 'tenant_invite' });
      track(ANALYTICS_EVENTS.TENANT_INVITED, {
        lease_id: generatedDocument.leaseId,
        invite_method: 'email' as const,
      });
      setState(prev => ({
        ...prev,
        isLoading: false,
        generatedDocument: prev.generatedDocument
          ? { ...prev.generatedDocument, ...updated }
          : prev.generatedDocument,
      }));
    } catch (err) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to send for signing',
      }));
    }
  }, [state, track]);

  return {
    state,
    goToStep,
    updateInputs,
    setSelectedTenantIds,
    setError,
    loadJurisdiction,
    loadDraft,
    loadDefaults,
    saveDraft,
    previewLease,
    generateLease,
    sendForSigning,
    updatePets,
    addAnimal,
    updateAnimal,
    removeAnimal,
    addAssistanceAnimal,
    updateAssistanceAnimal,
    removeAssistanceAnimal,
    addCoSigner,
    updateCoSigner,
    removeCoSigner,
    updateParking,
    updateUtilities,
    setShortTermRentalProhibited,
    updateSmokingPolicy,
    updateRentersInsurance,
    updateLandlordContact,
    initLandlordContact,
    setEditedContent,
    validateAnchors,
    applyPreFill,
  };
}
